import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

/**
 * Generic slide-over shell: header with a title and a count, a scrolling body, an empty state and
 * a footer note. Knows nothing about what it lists, so any future tray can reuse it.
 *
 * Dismissal lives here rather than in the host: Escape and a click outside both emit `closed$`,
 * and the host owns the `*ngIf`. Because the host only creates the shell once it is open, the
 * `mousedown` of the click that opened it has already happened — so the tray cannot close itself
 * on the very gesture that opened it.
 *
 * @example
 * ```html
 * <gains-tray *ngIf="isOpen" heading="Anomalies to review" [count]="waitingCount" (closed$)="close()">
 *   <gains-alert-card *ngFor="let alert of alerts" [alert]="alert"></gains-alert-card>
 * </gains-tray>
 * ```
 */
@Component({
  selector: 'gains-tray',
  standalone: false,
  templateUrl: './gains-tray.component.html',
  styleUrl: './gains-tray.component.scss'
})
export class GainsTrayComponent implements AfterViewInit {
  /**
   * Header title. Also names the tray for assistive technology.
   *
   * Not `title`: that is a global HTML attribute, so a host binding would also hang a browser
   * tooltip off the whole card.
   */
  @Input() heading = '';

  /** How many items are waiting. Hidden at zero, like the rail badge. */
  @Input() count = 0;

  /** True when there is nothing to list, which shows the empty state instead of the body. */
  @Input() isEmpty = false;

  @Input() emptyMessage = 'Nothing waiting for review.';

  /** Standing note under the list, e.g. how the list is ordered. */
  @Input() footerNote = '';

  @Output() closed$ = new EventEmitter<void>();

  constructor(private readonly _elementRef: ElementRef<HTMLElement>) {}

  /** Moves focus into the tray so Escape and a screen reader both land on what just opened. */
  ngAfterViewInit(): void {
    this._elementRef.nativeElement.querySelector<HTMLElement>('.tray')?.focus();
  }

  get hasCount(): boolean {
    return this.count > 0;
  }

  /** A note about how the list is ordered says nothing over an empty one. */
  get hasFooterNote(): boolean {
    return !!this.footerNote && !this.isEmpty;
  }

  close(): void {
    this.closed$.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.isOutsideTray(event.target)) {
      this.close();
    }
  }

  private isOutsideTray(target: EventTarget | null): boolean {
    return target instanceof Node && !this._elementRef.nativeElement.contains(target);
  }
}
